import { Alert, AlertDescription } from "~/components/ui/alert";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "~/components/ui/form";
import { Input } from "~/components/ui/input";
import { Switch } from "~/components/ui/switch";
import { Textarea } from "~/components/ui/textarea";

import { NumberField, SettingsSection, ToggleField } from "./form-fields";
import type { SectionProps } from "./types";

export function SlugSection({ form, onUpdate }: SectionProps) {
  return (
    <SettingsSection
      description="The web address customers will use to order"
      title="Storefront URL"
    >
      <FormField
        control={form.control}
        name="slug"
        render={({ field }) => (
          <FormItem>
            <FormLabel>URL Slug</FormLabel>
            <FormControl>
              <Input
                {...field}
                onBlur={() => onUpdate()}
                placeholder="your-restaurant"
              />
            </FormControl>
            <p className="text-muted-foreground text-xs">
              {field.value
                ? `${field.value}.plasma.app`
                : "your-restaurant.plasma.app"}
            </p>
            <FormMessage />
          </FormItem>
        )}
      />
    </SettingsSection>
  );
}

export function GoLiveSection({ form, onUpdate }: SectionProps) {
  return (
    <SettingsSection
      description="Enable or disable online ordering for customers"
      title="Go Live"
    >
      <FormField
        control={form.control}
        name="isLive"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="flex items-center gap-2">
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={(v) => {
                    field.onChange(v);
                    onUpdate();
                  }}
                />
              </FormControl>
              <span className="subtitle-sm">
                Online Ordering is {field.value ? "Live" : "Off"}
              </span>
            </FormLabel>
            <FormMessage />
          </FormItem>
        )}
      />
      {!form.watch("isLive") && (
        <Alert className="mt-3">
          <AlertDescription>
            Customers cannot see your storefront until online ordering is live.
          </AlertDescription>
        </Alert>
      )}
    </SettingsSection>
  );
}

export function FutureOrdersSection({ form, onUpdate }: SectionProps) {
  return (
    <SettingsSection
      description="Allow customers to schedule orders ahead"
      title="Future Orders"
    >
      <ToggleField
        description="Customers can pick a future time slot"
        form={form}
        label="Enable Scheduled Orders"
        name="futureOrdersEnabled"
        onUpdate={onUpdate}
      />
      {form.watch("futureOrdersEnabled") && (
        <div className="mt-3 pl-13">
          <NumberField
            form={form}
            label="Max Days Ahead"
            name="futureOrderMaxDays"
            onUpdate={onUpdate}
          />
        </div>
      )}
    </SettingsSection>
  );
}

export function AutoAcceptSection({ form, onUpdate }: SectionProps) {
  return (
    <SettingsSection
      description="Automatically accept incoming orders"
      title="Auto-Accept"
    >
      <ToggleField
        description="Orders are accepted without manual review"
        form={form}
        label="Auto-Accept Orders"
        name="autoAcceptOrders"
        onUpdate={onUpdate}
      />
      {form.watch("autoAcceptOrders") && (
        <div className="mt-3 pl-13">
          <NumberField
            form={form}
            label="Delay (seconds)"
            name="autoAcceptDelaySeconds"
            onUpdate={onUpdate}
          />
        </div>
      )}
    </SettingsSection>
  );
}

export function TipsSection({ form, onUpdate }: SectionProps) {
  return (
    <SettingsSection
      description="Configure tip options shown to customers"
      title="Tips"
    >
      <FormField
        control={form.control}
        name="tipOptions"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Tip Percentages</FormLabel>
            <div className="flex flex-wrap gap-2">
              {[10, 15, 18, 20, 25].map((pct) => {
                const isSelected = field.value.includes(pct);
                return (
                  <button
                    className={`rounded-full border px-4 py-1.5 text-sm font-medium ${
                      isSelected
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border text-muted-foreground"
                    }`}
                    key={pct}
                    onClick={() => {
                      const next = isSelected
                        ? field.value.filter((v) => v !== pct)
                        : [...field.value, pct].toSorted((a, b) => a - b);
                      field.onChange(next);
                      onUpdate();
                    }}
                    type="button"
                  >
                    {pct}%
                  </button>
                );
              })}
            </div>
            <FormMessage />
          </FormItem>
        )}
      />
      <div className="mt-3">
        <NumberField
          form={form}
          label="Default Tip %"
          name="defaultTipPercent"
          onUpdate={onUpdate}
        />
      </div>
    </SettingsSection>
  );
}

export function PauseSection({ form, onUpdate }: SectionProps) {
  return (
    <SettingsSection
      description="Temporarily pause online ordering"
      title="Pause Ordering"
    >
      <ToggleField
        description="Customers will see a paused message"
        form={form}
        label="Pause Online Ordering"
        name="isPaused"
        onUpdate={onUpdate}
      />
      {form.watch("isPaused") && (
        <div className="mt-3 space-y-3 pl-13">
          <FormField
            control={form.control}
            name="pauseMessage"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Pause Message</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    onBlur={() => onUpdate()}
                    placeholder="We're taking a short break. Check back soon!"
                    rows={2}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="pauseUntil"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Auto-Resume At</FormLabel>
                <FormControl>
                  <Input
                    onBlur={() => onUpdate()}
                    onChange={(e) => field.onChange(e.target.value)}
                    type="datetime-local"
                    value={field.value}
                  />
                </FormControl>
                <p className="text-muted-foreground text-xs">
                  Leave empty to keep paused until manually resumed
                </p>
                <FormMessage />
              </FormItem>
            )}
          />
        </div>
      )}
    </SettingsSection>
  );
}
